import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import GroupsPage from 'model/GroupsPage'
import Group from 'model/Group'
import GroupsPagesRepository from 'clients/db/GroupsPagesRepository'
import GroupRepository from 'clients/db/GroupRepository'
import GroupPatient from 'model/GroupPatient'
import Api from 'clients/api/GeneralApi'
import GroupUser from 'model/GroupUser'

interface GroupState {
  isLoading: boolean
  groupsPages: GroupsPage[]
  group: Group
  groupPatients: GroupPatient[]
  groupUsers: GroupUser[]
  allGroups: Group[]
}

const initialState: GroupState = {
  isLoading: false,
  groupsPages: [],
  group: {} as Group,
  groupPatients: [],
  groupUsers: [],
  allGroups: [],
}

function startLoading(state: GroupState) {
  state.isLoading = true
}

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    fetchGroupsStart: startLoading,
    updateGroupStart: startLoading,
    fetchGroupsFirstPageSuccess: (state, { payload }: PayloadAction<GroupsPage>) => {
      state.isLoading = false
      state.groupsPages = [payload]
    },
    fetchGroupsPageSuccess: (state, { payload }: PayloadAction<GroupsPage>) => {
      state.isLoading = false
      state.groupsPages = [...state.groupsPages, payload]
    },
    fetchGroupSuccess: (state, { payload }: PayloadAction<Group>) => {
      state.isLoading = false
      state.group = payload
    },
    fetchAllGroupsSuccess: (state, { payload }: PayloadAction<Group[]>) => {
      state.isLoading = false
      state.allGroups = payload
    },
    updateGroupSuccess(state, { payload }: PayloadAction<Group>) {
      state.isLoading = false
      state.group = payload
    },
    updateGroupFailed(state: GroupState) {
      state.isLoading = false
    },
    fetchGroupPatientsSuccess: (state, { payload }: PayloadAction<GroupPatient[]>) => {
      state.isLoading = false
      state.groupPatients = payload
    },
    fetchGroupUsersSuccess: (state, { payload }: PayloadAction<GroupUser[]>) => {
      state.isLoading = false
      state.groupUsers = payload
    },
    removeGroup(state: GroupState) {
      state.group = {} as Group
    },
  },
})
export const {
  fetchGroupSuccess,
  fetchGroupsFirstPageSuccess,
  fetchGroupsPageSuccess,
  fetchAllGroupsSuccess,
  fetchGroupsStart,
  updateGroupFailed,
  updateGroupSuccess,
  updateGroupStart,
  fetchGroupPatientsSuccess,
  fetchGroupUsersSuccess,
  removeGroup,
} = groupsSlice.actions

export const fetchGroupsPage = (bookmark: string, searchString?: string): AppThunk => async (
  dispatch,
) => {
  dispatch(fetchGroupsStart())

  let groupsPages
  if (searchString === undefined || searchString.trim() === '') {
    groupsPages = await GroupsPagesRepository.searchGroupsPage(bookmark)
  } else {
    groupsPages = await GroupsPagesRepository.searchGroupsPageText(bookmark, searchString)
  }

  if (bookmark === '') {
    dispatch(fetchGroupsFirstPageSuccess(groupsPages))
  } else {
    dispatch(fetchGroupsPageSuccess(groupsPages))
  }
}

export const fetchMainGroupsPage = (bookmark: string, searchString?: string): AppThunk => async (
  dispatch,
) => {
  dispatch(fetchGroupsStart())

  let groupsPages
  if (searchString === undefined || searchString.trim() === '') {
    groupsPages = await GroupsPagesRepository.searchMainGroupsPage(bookmark)
  } else {
    groupsPages = await GroupsPagesRepository.searchMainGroupsPageText(bookmark, searchString)
  }

  if (bookmark === '') {
    dispatch(fetchGroupsFirstPageSuccess(groupsPages))
  } else {
    dispatch(fetchGroupsPageSuccess(groupsPages))
  }
}

export const fetchGroup = (id: string): AppThunk => async (dispatch) => {
  dispatch(fetchGroupsStart())
  const group = await GroupRepository.find(id)
  dispatch(fetchGroupSuccess(group))
}

export const fetchAllGroups = (): AppThunk => async (dispatch) => {
  dispatch(fetchGroupsStart())
  const allGroups = await GroupRepository.findAll()
  dispatch(fetchAllGroupsSuccess(allGroups))
}

export const createGroup = (
  group: Group,
  onSuccess?: (group: Group) => void,
  onError?: (error: any) => void,
): AppThunk => async (dispatch) => {
  dispatch(updateGroupStart())

  try {
    const createdGroup = await GroupRepository.saveOrUpdate(group)
    console.log('createdGroup', createdGroup)

    dispatch(updateGroupSuccess(createdGroup))
    if (onSuccess) {
      onSuccess(createdGroup)
    }
  } catch (error) {
    dispatch(updateGroupFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const updateGroup = (
  group: Group,
  onSuccess?: (group: Group) => void,
  onError?: (error: any) => void,
): AppThunk => async (dispatch) => {
  dispatch(updateGroupStart())
  console.log('updateGroupStart')

  try {
    const updatedGroup = await GroupRepository.saveOrUpdate(group)
    console.log('updatedGroup', updatedGroup)

    dispatch(updateGroupSuccess(updatedGroup))
    if (onSuccess) {
      onSuccess(updatedGroup)
    }
  } catch (error) {
    dispatch(updateGroupFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const loadGroupPatients = (group: Group): AppThunk => async (dispatch) => {
  try {
    Api.fetchGroupPatients(group)
      .then((response) => {
        dispatch(fetchGroupPatientsSuccess(response.data.docs))
      })
      .catch((error) => {
        console.log(error)
      })
  } catch (error) {}
}

export const loadGroupUsers = (group: Group): AppThunk => async (dispatch) => {
  try {
    Api.fetchGroupUsers(group)
      .then((response) => {
        dispatch(fetchGroupUsersSuccess(response.data.docs))
      })
      .catch((error) => {
        console.log(error)
      })
  } catch (error) {}
}

export default groupsSlice.reducer
